import {Dropdown} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {FreeProductReward} from '@/types/promotion'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_ID, buyOptions, BuyType} from '@/utils/constants/promotionConstants'
import {FC, useState} from 'react'
import PromotionSelectionModal from '../../PromotionSelectionModal'
import {RewardItemProps} from './RewardItem'
import {useRewardValidations} from './RewardValidations.js'

export const freeProductTypeOptions = [
  {displayText: 'Product ID', id: BuyType.PRODUCT_ID},
  {displayText: 'GTIN', id: BuyType.GTINS},
]

export const RewardFreeProducts: FC<RewardItemProps> = ({promotionCondition, index}) => {
  const {updateCondition, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasValueLayerError} = useRewardValidations(promotionCondition)
  const {conditions} = promotionConfig
  const buyTypeOptions = (conditions?.itemTypeOptions ?? []).filter(option => option.id !== ANY_ID)
  const reward = (promotionCondition?.rewards?.[0] ?? {}) as FreeProductReward

  const [modalProps, setModalProps] = useState({show: false, type: ''})

  const buyTypesIds = buyTypeOptions?.map(item => item.id)

  const updateReward = (update: Partial<FreeProductReward & {selectedItems?: {id: string; displayText: string}[]}>) => {
    const currentReward = promotionCondition?.rewards?.[0] ?? {}

    const mergedReward = {
      ...currentReward,
      ...update,
    } as FreeProductReward

    updateCondition({
      ...promotionCondition,
      rewards: [mergedReward],
    })
  }

  const handleBuyCount = value => {
    if (value?.[0]?.id) {
      updateReward({quantity: Number(value?.[0]?.id)})
    }
  }

  const clearValues = () => {
    updateReward({items: []})
  }

  const handleSelectedProductType = values => {
    if (!values || !values.length) {
      clearValues()
      return
    }

    const isModalValue = values.find(item => buyTypesIds.includes(item.id))

    if (isModalValue) {
      if (isModalValue.id === ANY_ID) {
        clearValues()
        return
      }
      setModalProps({show: true, type: isModalValue.id})
    } else {
      // Directly update when user adds/removes regular items
      const items = values?.map(item => ({
        id: item.id,
        name: item.displayText,
      }))
      const selectedItemsFormatted = values.map(item => ({
        id: item.id,
        displayText: item.displayText,
      }))
      updateReward({items, selectedItems: selectedItemsFormatted})
    }
  }

  const handleCloseModal = () => {
    setModalProps(prev => ({...prev, show: false}))
  }

  const handleSubmitModal = values => {
    const selectedItems = values?.values || []
    const itemType = values.itemType
    const items = selectedItems.map(item => ({
      id: item.id,
      name: item.displayText,
    }))
    updateReward({items, selectedItems, itemType})
    handleCloseModal()
  }

  const items = reward?.items ?? []

  // Use selectedItems for display if available, otherwise fall back to items
  const selectedValuesText = items.map(item => item.name).join(', ') || ''
  const quantityValue = buyOptions.filter(item => item.id === reward.quantity?.toString()) ?? []

  // Use selectedItems for dropdown value if available, otherwise convert items
  const productsValue = items?.map(item => ({id: item.id, displayText: item.name})) ?? []

  const isError = hasValueLayerError()

  return (
    <div className="flex items-start gap-4">
      <Dropdown
        isJsonWrap={false}
        childList={buyOptions}
        placeholder={ANY_ID}
        updateValue={handleBuyCount}
        isClearable={false}
        autocomplete="off"
        value={quantityValue}
        uiSchema={{style: {maxWidth: '120px'}}}
        loading={isLoadingConfig}
        isError={isError}
        errorMessage="Value Invalid"
      />
      <Dropdown
        isJsonWrap={false}
        childList={freeProductTypeOptions}
        placeholder={selectedValuesText || Constants.AddProducts}
        value={[...productsValue]}
        updateValue={handleSelectedProductType}
        isClearable={true}
        autocomplete="off"
        loading={isLoadingConfig}
        selection="multiple"
      />
      <PromotionSelectionModal
        key={JSON.stringify(modalProps.type)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          values: productsValue,
          itemType: reward.itemType || '',
        }}
      />
    </div>
  )
}
